import { ReactNode, useRef, useState } from 'react'
import { styled } from '@mui/material'
import { colorPalette } from '../../../../style/colorPalette'
import { TextInput } from './TextInput'

type InlineEditTextProps = {
  value: string | undefined
  display: ReactNode
  onChange: (v: string | undefined) => void
  onBlur: (v: string | undefined, changed: boolean) => void
  editable: boolean
  variant: 'header' | 'leadSentence'
  multiline?: boolean
  placeholder?: string
  validate?: (v: string | undefined) => boolean
}

export const InlineEditText = ({
  value,
  display,
  onChange,
  onBlur,
  editable,
  variant,
  multiline,
  placeholder,
  validate,
}: InlineEditTextProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const startValueRef = useRef<string | undefined>(value)

  if (!editable || !isEditing) {
    return (
      <DisplayArea
        align={variant === 'header' ? 'center' : 'flex-start'}
        onDoubleClick={() => {
          if (!editable) return
          startValueRef.current = value
          setIsEditing(true)
        }}
      >
        {display}
      </DisplayArea>
    )
  }

  return (
    <EditArea
      align={variant === 'header' ? 'center' : 'flex-start'}
      onBlur={() => setIsEditing(false)}
    >
      <TextInput
        value={value}
        onChange={onChange}
        onBlur={v => {
          if (validate && !validate(v)) {
            onChange(startValueRef.current)
            return
          }
          onBlur(v, (v ?? '') !== (startValueRef.current ?? ''))
        }}
        variant={variant}
        multiline={multiline}
        placeholder={placeholder}
        autoFocus={true}
      />
    </EditArea>
  )
}

const boxBase = {
  width: '100%',
  height: '100%',
  borderRadius: '4px',
  border: '1px solid transparent',
  padding: '4px',
  boxSizing: 'border-box' as const,
}

const DisplayArea = styled('div')<{ align: string }>(({ align }) => ({
  ...boxBase,
  display: 'flex',
  alignItems: align,
  cursor: 'default',
}))

const EditArea = styled('div')<{ align: string }>(({ align }) => ({
  ...boxBase,
  display: 'flex',
  alignItems: align,
  borderColor: colorPalette.monotone[2],
}))
